import React, { useEffect, useMemo, useRef, useState } from "react";
import Project from "components/projects/Project";
import { Project as ProjectModel } from "models/projects/Project";
import { Team } from "models/teams/Team";
import { ApiService } from "services/ApiService";

type SortColumn = "name" | "description" | "user" | "status" | "lastUpdated";
type SortDirection = "asc" | "desc";

export type OperationsHubProps = {
  team: Team;
  artistId: string;
  outlet?: React.ReactNode;
};

const OperationsHub: React.FC<OperationsHubProps> = ({ team, artistId, outlet }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [projects, setProjects] = useState<ProjectModel[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const projectViewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!team?.teamId) return;

      try {
        setLoading(true);
        const resp = await ApiService.getProjects(artistId ?? "", team.teamId);
        if (!resp || !resp.data) {
          setProjects([]);
          return;
        }
        setProjects(resp.data);
      } catch (err) {
        console.error("Failed to fetch projects", err);
      } finally {
        setLoading(false);
      }
    };

    void fetchProjects();
  }, [artistId, team?.teamId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (projectViewRef.current && !projectViewRef.current.contains(event.target as Node)) {
        setSelectedProjectId(null);
      }
    };

    if (selectedProjectId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }

    return;
  }, [selectedProjectId]);

  const resolveLastUpdated = (p: ProjectModel): string => {
    const anyP: any = p as any;
    const direct = anyP.lastUpdated || anyP.updatedAt || anyP.dateUpdated;
    if (direct) return new Date(direct).toLocaleString();

    const timestamps: number[] = [];
    (p.enrollmentSubmissions || []).forEach((es) => {
      const ds = (es.userSubmission as any)?.dateSubmitted;
      const dc = (es.userSubmission as any)?.dateCompleted;
      if (ds) timestamps.push(new Date(ds).getTime());
      if (dc) timestamps.push(new Date(dc).getTime());
    });

    const latest = timestamps.length ? new Date(Math.max(...timestamps)).toLocaleString() : "—";
    return latest;
  };

  const resolveStatus = (p: any): string => {
    const status = typeof p.status === "number" ? p.status : Number(p.status ?? -1);
    switch (status) {
      case 0:
        return "In Progress";
      case 1:
        return "Denied";
      case 2:
        return "Completed";
      default:
        return "User submitted, pending review";
    }
  };

  const handleRowClick = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const filteredAndSortedProjects = useMemo(() => {
    let filtered = projects;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = projects.filter((p) => {
        const name = (p.name || "").toLowerCase();
        const description = (p.description || "").toLowerCase();
        const user = (p.ownerName || "").toLowerCase();
        const status = resolveStatus(p).toLowerCase();
        return (
          name.includes(term) ||
          description.includes(term) ||
          user.includes(term) ||
          status.includes(term)
        );
      });
    }

    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | number = "";
      let bValue: string | number = "";

      switch (sortColumn) {
        case "name":
          aValue = a.name || "";
          bValue = b.name || "";
          break;
        case "description":
          aValue = a.description || "";
          bValue = b.description || "";
          break;
        case "user":
          aValue = a.ownerName || "";
          bValue = b.ownerName || "";
          break;
        case "status":
          aValue = resolveStatus(a);
          bValue = resolveStatus(b);
          break;
        case "lastUpdated":
          aValue = resolveLastUpdated(a);
          bValue = resolveLastUpdated(b);
          break;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [projects, searchTerm, sortColumn, sortDirection]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-4">Operations Hub</h1>
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-4">Operations Hub</h1>
      {team && <h2 className="text-lg mb-2">{team.teamName}</h2>}
      <p className="text-sm text-gray-600 mb-6">
        Manage projects, review submissions, and coordinate with your team.
      </p>

      {selectedProjectId ? (
        <div ref={projectViewRef} className="bg-white rounded-lg shadow-lg p-4">
          <button
            onClick={() => setSelectedProjectId(null)}
            className="mb-4 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            ← Back to Projects
          </button>
          <Project projectId={selectedProjectId} />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold">Projects</h2>
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort("name")}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {sortColumn === "name" && (
                        <span className="text-blue-500">{sortDirection === "asc" ? "↑" : "↓"}</span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("description")}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  >
                    <div className="flex items-center gap-1">
                      Description
                      {sortColumn === "description" && (
                        <span className="text-blue-500">{sortDirection === "asc" ? "↑" : "↓"}</span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("user")}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  >
                    <div className="flex items-center gap-1">
                      User
                      {sortColumn === "user" && (
                        <span className="text-blue-500">{sortDirection === "asc" ? "↑" : "↓"}</span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("status")}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {sortColumn === "status" && (
                        <span className="text-blue-500">{sortDirection === "asc" ? "↑" : "↓"}</span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("lastUpdated")}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  >
                    <div className="flex items-center gap-1">
                      Last Updated
                      {sortColumn === "lastUpdated" && (
                        <span className="text-blue-500">{sortDirection === "asc" ? "↑" : "↓"}</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAndSortedProjects.map((p) => {
                  return (
                    <tr
                      key={p.projectId}
                      onClick={() => handleRowClick(p.projectId)}
                      className="hover:bg-gray-100 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {p.name || `${p.ownerName}'s Project` || "—"}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">{p.description || "—"}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{p.ownerName || "—"}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{resolveStatus(p)}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{resolveLastUpdated(p)}</td>
                    </tr>
                  );
                })}
                {filteredAndSortedProjects.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                      {searchTerm ? `No projects match "${searchTerm}"` : "No projects found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {outlet}
    </div>
  );
};

export default OperationsHub;
